import { useEffect, useRef, useCallback } from "react";
import { BusinessListing } from "@/types/directory";
import BusinessCard from "./BusinessCard";
import { Loader2, Building2 } from "lucide-react";

interface BusinessGridProps {
  businesses: BusinessListing[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  searchQuery?: string;
}

// Skeleton loader for business cards
const BusinessCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-grey-100 animate-pulse">
    <div className="h-32 bg-grey-100" />
    <div className="p-5">
      <div className="h-5 bg-grey-100 rounded w-3/4 mb-3" />
      <div className="h-4 bg-grey-100 rounded w-1/2 mb-3" />
      <div className="h-4 bg-grey-100 rounded w-full mb-2" />
      <div className="h-4 bg-grey-100 rounded w-2/3 mb-3" />
      <div className="flex gap-2 mb-3">
        <div className="h-5 bg-grey-100 rounded-full w-16" />
        <div className="h-5 bg-grey-100 rounded-full w-20" />
      </div>
      <div className="pt-3 border-t border-grey-100">
        <div className="h-4 bg-grey-100 rounded w-24" />
      </div>
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ searchQuery }: { searchQuery?: string }) => (
  <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
    <div className="h-16 w-16 bg-grey-100 rounded-full flex items-center justify-center mb-4">
      <Building2 className="h-8 w-8 text-grey-400" />
    </div>
    <h3 className="text-lg font-semibold text-grey-900 mb-2">
      {searchQuery ? "No businesses found" : "No businesses available"}
    </h3>
    <p className="text-grey-500 text-center max-w-md">
      {searchQuery
        ? `We couldn't find any businesses matching "${searchQuery}". Try a different search term.`
        : "There are currently no businesses accepting online bookings. Check back later!"}
    </p>
  </div>
);

const BusinessGrid = ({
  businesses,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  searchQuery,
}: BusinessGridProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Set up intersection observer for infinite scroll
  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          onLoadMore();
        }
      },
      {
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
  }, [hasMore, loadingMore, loading, onLoadMore]);

  useEffect(() => {
    setupObserver();

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupObserver]);

  // Initial loading state
  if (loading && businesses.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <BusinessCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  // Empty state
  if (!loading && businesses.length === 0) {
    return <EmptyState searchQuery={searchQuery} />;
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {businesses.map((business) => (
          <BusinessCard key={business.id} business={business} />
        ))}

        {/* Loading more skeletons */}
        {loadingMore &&
          Array.from({ length: 4 }).map((_, index) => (
            <BusinessCardSkeleton key={`loading-${index}`} />
          ))}
      </div>

      {/* Load more trigger element */}
      {hasMore && !loadingMore && (
        <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-green-300 animate-spin opacity-0" />
        </div>
      )}

      {/* Loading indicator when fetching more */}
      {loadingMore && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 text-green-300 animate-spin" />
          <span className="ml-2 text-grey-500 text-sm">Loading more...</span>
        </div>
      )}

      {/* End of list indicator */}
      {!hasMore && businesses.length > 0 && (
        <div className="text-center py-4">
          <p className="text-grey-400 text-sm">You've reached the end</p>
        </div>
      )}
    </div>
  );
};

export default BusinessGrid;
